import React from "react";
import { fmtDate } from "../../lib/format";
import {
  canManageMember,
  canReceiveOwnership,
  roleLabel,
  stateLabel,
} from "../../lib/memberManagement";

/**
 * The people of the organization.
 *
 * The rows are exactly what the database chose to return: an Owner receives
 * every member including deactivated ones, a Manager receives its own row plus
 * the Sales members and never the Owner or another Manager. This component does
 * no filtering at all, so it cannot widen the view by accident.
 *
 * THE ACTIONS COLUMN (Phase 2D.2 checkpoint 3) appears for an OWNER only, and
 * only on a Manager or a Sales row. The Owner row and the acting person's own row
 * carry no lifecycle controls, and a Manager or a Sales user is never given these
 * buttons at all - the backend and the database refuse them regardless.
 *
 * "Прехвърли собствеността" (Phase 2D.2 checkpoint 4) is offered on the same rows
 * but only while the person is ACTIVE, because an inactive member may not receive
 * the organization. Whether the transfer is actually possible - the outgoing
 * Owner needs a licensed and free Търговец seat - is answered by the database and
 * shown inside the confirmation, never guessed here.
 *
 * A state is never communicated by colour alone: "Активен" / "Неактивен" is
 * always spelled out next to the dot.
 */
export const MemberRoster = ({
  items,
  loading,
  error,
  actorRole,
  busyEmail,
  onDeactivate,
  onReactivate,
  onChangeRole,
  onTransferOwnership,
}) => {
  if (loading) {
    return (
      <div className="qw-card qw-empty" data-testid="member-roster-loading">
        Зареждане на потребителите…
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="qw-card qw-empty"
        style={{ borderColor: "#fecaca", color: "#7f1d1d", background: "#fef2f2" }}
        data-testid="member-roster-error"
      >
        {error}
      </div>
    );
  }
  if (!items || items.length === 0) {
    return (
      <div className="qw-card qw-empty" data-testid="member-roster-empty">
        Няма потребители за показване.
      </div>
    );
  }

  const showActions = actorRole === "owner";

  return (
    <div
      className="qw-table-wrap"
      tabIndex={0}
      role="region"
      aria-label="Потребители на организацията"
    >
      <table className="qw-table" data-testid="member-roster-table">
        <thead>
          <tr>
            <th scope="col">Име</th>
            <th scope="col">Имейл</th>
            <th scope="col">Телефон</th>
            <th scope="col">Роля</th>
            <th scope="col">Статус</th>
            <th scope="col">Член от</th>
            {showActions && <th scope="col">Действия</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((member) => {
            const name =
              [member.first_name, member.last_name].filter(Boolean).join(" ") ||
              member.full_name ||
              "—";
            const manageable = canManageMember(actorRole, member);
            const transferable = canReceiveOwnership(actorRole, member);
            const busy = busyEmail === member.email;
            return (
              <tr key={member.member_id || member.email} data-testid={`member-row-${member.email}`}>
                <td>
                  {name}
                  {member.is_self && (
                    <span className="qw-tag" data-testid="member-row-self">
                      Вие
                    </span>
                  )}
                </td>
                <td style={{ overflowWrap: "anywhere" }}>{member.email}</td>
                <td>{member.phone || "—"}</td>
                <td data-testid={`member-row-role-${member.email}`}>
                  {member.role_label || roleLabel(member.role)}
                </td>
                <td>
                  <span
                    className={`qw-badge ${member.is_active ? "accepted" : "archived"}`}
                    data-testid={`member-row-state-${member.email}`}
                  >
                    <span className="dot" aria-hidden="true" />
                    {member.state_label || stateLabel(member.is_active)}
                  </span>
                </td>
                <td>{member.member_since ? fmtDate(member.member_since) : "—"}</td>
                {showActions && (
                  <td>
                    {manageable ? (
                      <div className="qw-row-actions">
                        {member.is_active ? (
                          <button
                            type="button"
                            className="qw-btn ghost sm"
                            disabled={busy}
                            onClick={() => onDeactivate(member)}
                            data-testid={`member-deactivate-${member.email}`}
                          >
                            Деактивирай
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="qw-btn ghost sm"
                            disabled={busy}
                            onClick={() => onReactivate(member)}
                            data-testid={`member-reactivate-${member.email}`}
                          >
                            Активирай
                          </button>
                        )}
                        <button
                          type="button"
                          className="qw-btn ghost sm"
                          disabled={busy}
                          onClick={() => onChangeRole(member)}
                          data-testid={`member-change-role-${member.email}`}
                        >
                          Смени роля
                        </button>
                        {transferable && (
                          <button
                            type="button"
                            className="qw-btn ghost sm"
                            disabled={busy}
                            onClick={() => onTransferOwnership(member)}
                            data-testid={`member-transfer-ownership-${member.email}`}
                          >
                            Прехвърли собствеността
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="qw-help" data-testid={`member-no-actions-${member.email}`}>
                        —
                      </span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MemberRoster;
